import { paths } from '@app/routes/paths';
import { useLogoutMutation, useRefreshSessionMutation } from '@features/auth/authApi';
import { useSessionExpiry } from '@features/auth/hooks/useSessionExpiry';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const WARN_SECONDS = 60;

/**
 * Mounts a hidden dialog that appears when the access token has ≤ WARN_SECONDS
 * of life left. Offers the user a chance to silently refresh, or log out.
 * Only mount inside authenticated regions of the tree.
 */
export function SessionExpiryWarning() {
  const { secondsRemaining, warning } = useSessionExpiry({ warnSeconds: WARN_SECONDS });
  const [refresh, { isLoading: isRefreshing }] = useRefreshSessionMutation();
  const [logout] = useLogoutMutation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // `dismissedForSecs` remembers the `secondsRemaining` value at which the
  // user dismissed the dialog. We only stay dismissed while time is still
  // ticking *down* from that moment — after a silent refresh the token's
  // remaining time jumps back up, so we reopen the dialog on next expiry.
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const isDismissed =
    dismissedAt !== null && secondsRemaining !== null && secondsRemaining <= dismissedAt;

  const open = warning && !isDismissed;
  const secs = Math.max(0, secondsRemaining ?? 0);
  const progress = Math.min(100, ((WARN_SECONDS - secs) / WARN_SECONDS) * 100);

  const handleExtend = async () => {
    try {
      await refresh().unwrap();
      // The refresh pushed `secondsRemaining` back up, so no need to remember
      // a dismissal point — the dialog will naturally hide.
      setDismissedAt(null);
    } catch {
      /* reauth middleware handles hard failure */
    }
  };

  const handleLogout = async () => {
    await logout();
    await navigate(paths.login, { replace: true });
  };

  const handleClose = () => {
    setDismissedAt(secondsRemaining ?? 0);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('auth.session.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>{t('auth.session.body')}</DialogContentText>
          <LinearProgress
            variant="determinate"
            value={progress}
            aria-label={t('auth.session.title')}
          />
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
            {t('auth.session.expiresInSeconds', { count: secs })}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            void handleLogout();
          }}
          color="inherit"
        >
          {t('auth.session.logOut')}
        </Button>
        <Button
          onClick={() => {
            void handleExtend();
          }}
          variant="contained"
          disabled={isRefreshing}
        >
          {t('auth.session.stay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
